import type { ReactNode } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { selectUser } from "@/features/auth/store/sessionSlice";
import AccessDeniedPage from "@/shared/components/ErrorPage/AccessDeniedPage";
import ErrorPage from "@/shared/components/ErrorPage/ErrorPage";
import Loading from "@/shared/components/Loading";
import UpsellPage from "@/shared/components/UpsellPage/UpsellPage";
import { PREMIUM_FEATURE_KEYS } from "@/shared/components/UpsellPage/configs/upgradeFeatures.config";
import type { PlanTier } from "@/shared/components/UpsellPage/types/upsellPage.types";
import { findRouteByPath, checkFeatureRequirement, checkRouteReadPermission } from "@/shared/configs/routeRegistry.config";
import type { RouteFeatureRequirement } from "@/shared/configs/routeRegistry.config";
import { useOrganizationFeatures } from "@/shared/hooks/useOrganizationFeatures";
import { useRepresentativePermissions } from "@/shared/hooks/useRepresentativePermissions";
import { getContentFeatureAvailability, isContentFeatureAvailable } from "@/shared/utils/contentFeatureAvailability.utils";
import { getUpsellCategoryFromRoute, getUpsellMetadata } from "@/shared/utils/upsell.utils";

const getFeatureTier = (requirement?: RouteFeatureRequirement): PlanTier => {
  if (!requirement) return "standard";
  if (typeof requirement === "string") {
    return PREMIUM_FEATURE_KEYS.has(requirement as never) ? "premium" : "standard";
  }
  if ("feature" in requirement) {
    return PREMIUM_FEATURE_KEYS.has(requirement.feature as never) ? "premium" : "standard";
  }
  return "standard";
};

type FeatureProtectedRouteProps = { children: ReactNode };

const FeatureProtectedRoute = ({ children }: FeatureProtectedRouteProps) => {
  const location = useLocation();
  const representative = useSelector(selectUser)?.representative;
  const features = useOrganizationFeatures();
  const { permissions } = useRepresentativePermissions();

  const route = findRouteByPath(location.pathname);

  if (route?.requiresRepresentativeOrganization && (!representative?.id || !representative.organization_id)) {
    return <AccessDeniedPage />;
  }

  if (!route?.feature) {
    return <>{children}</>;
  }

  const planFeatures = features.plan?.features;
  if (route.feature === "service_polls" || route.feature === "service_surveys") {
    if (!planFeatures) {
      return features.isPlanLoading
        ? <Loading state={{ loading: true }} role="status" aria-label="Chargement des fonctionnalités" />
        : <ErrorPage />;
    }
    if (!isContentFeatureAvailable(route.feature, getContentFeatureAvailability(planFeatures))) return <ErrorPage />;
  }
  const hasAccess = checkFeatureRequirement(route.feature, planFeatures ?? {});

  if (!hasAccess) {
    if (planFeatures?.upsell_promotion && features.plan?.upsell_promotion) {
      const category = getUpsellCategoryFromRoute(route);
      const metadata = getUpsellMetadata(category, features.plan.upsell_promotion);
      if (metadata) {
        const targetTier = getFeatureTier(route.feature);
        return (
          <UpsellPage
            key={location.pathname}
            metadata={metadata}
            targetTier={targetTier}
            planName={targetTier === "premium" ? "Pack Premium" : "Pack Standard"}
          />
        );
      }
    }
    return <ErrorPage />;
  }

  if (permissions !== null && !checkRouteReadPermission(route, permissions)) {
    return <AccessDeniedPage />;
  }

  return <>{children}</>;
};

export default FeatureProtectedRoute;
